import { useCallback, useEffect, useRef, useState } from "react";
import api, { apiErr, openFile } from "@/lib/api";
import { PageHeader, Loading, EmptyState } from "@/components/Bits";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { ChecklistItem } from "@/components/movein/ChecklistItem";
import { ClipboardCheck, Home, Save, Send, Loader2, FileDown, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

const summarize = (rooms) => {
  const items = rooms.flatMap((r) => r.items);
  const count = (k) => items.filter((i) => i.condition === k).length;
  return { total: items.length, rated: items.filter((i) => i.condition).length, good: count("good"), fair: count("fair"), poor: count("poor"), na: count("na"), photos: items.reduce((n, i) => n + (i.photos?.length || 0), 0) };
};

function Summary({ s }) {
  const pct = s.total ? Math.round((s.rated / s.total) * 100) : 0;
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5" data-testid="checklist-summary">
      <div className="flex items-center justify-between text-sm mb-2">
        <span className="font-semibold text-slate-700">{s.rated} of {s.total} items rated</span>
        <span className="font-bold text-slate-900">{pct}%</span>
      </div>
      <Progress value={pct} className="h-2" />
      <div className="flex flex-wrap gap-3 mt-3 text-xs font-semibold">
        <span className="text-emerald-700">{s.good} good</span><span className="text-amber-700">{s.fair} fair</span>
        <span className="text-rose-700">{s.poor} poor</span><span className="text-slate-500">{s.na} n/a</span>
        <span className="text-slate-500 ml-auto">{s.photos} photo(s)</span>
      </div>
    </div>
  );
}

function SubmittedCard({ cl }) {
  const [busy, setBusy] = useState(false);
  const view = async () => {
    setBusy(true);
    try { await openFile(`/documents/${cl.document_id}/download`); } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5 flex flex-wrap items-center gap-4" data-testid="checklist-submitted">
      <span className="h-11 w-11 rounded-xl bg-emerald-600 text-white flex items-center justify-center"><CheckCircle2 className="h-5 w-5" /></span>
      <div className="flex-1 min-w-[200px]">
        <div className="font-display font-bold text-emerald-900">Checklist submitted</div>
        <div className="text-sm text-emerald-800/80">Signed on {new Date(cl.submitted_at).toLocaleDateString()} · saved to your Documents and shared with your landlord.</div>
      </div>
      {cl.document_id && (
        <Button variant="outline" onClick={view} disabled={busy} className="gap-2 bg-white" data-testid="checklist-view-pdf">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />} View PDF
        </Button>
      )}
    </div>
  );
}

const payload = (c) => ({ general_note: c.general_note || "", items: c.rooms.flatMap((r) => r.items.map((it) => ({ room_key: r.key, item_key: it.key, condition: it.condition, note: it.note || "" }))) });

export default function TenantMoveIn() {
  const [data, setData] = useState(null);
  const [cl, setCl] = useState(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const dirty = useRef(false);

  const load = useCallback(() => api.get("/tenant/move-in").then((r) => { setData(r.data); setCl(r.data.checklist); dirty.current = false; }), []);
  useEffect(() => { load(); }, [load]);

  const save = useCallback(async (c, quiet = false) => {
    setSaving(true);
    try {
      const { data: saved } = await api.put("/tenant/move-in", payload(c));
      setCl((prev) => ({ ...saved, rooms: saved.rooms.map((r, ri) => ({ ...r, items: r.items.map((it, ii) => ({ ...it, photos: prev?.rooms?.[ri]?.items?.[ii]?.photos || it.photos })) })) }));
      dirty.current = false;
      if (!quiet) toast.success("Progress saved");
    } catch (e) { toast.error(apiErr(e)); } finally { setSaving(false); }
  }, []);

  const patch = (roomKey, itemKey, p) => {
    dirty.current = true;
    setCl((c) => ({ ...c, rooms: c.rooms.map((r) => r.key !== roomKey ? r : { ...r, items: r.items.map((it) => it.key !== itemKey ? it : { ...it, ...p }) }) }));
  };

  const refreshPhotos = async () => {
    if (dirty.current) await save(cl, true);
    const { data: fresh } = await api.get("/tenant/move-in");
    setCl(fresh.checklist);
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      await save(cl, true);
      const { data: res } = await api.post("/tenant/move-in/submit");
      setCl(res.checklist);
      toast.success("Checklist submitted — PDF saved to your Documents");
    } catch (e) { toast.error(apiErr(e)); } finally { setSubmitting(false); }
  };

  if (!data) return <Loading />;
  if (!data.lease) return <div><PageHeader title="Move-in checklist" /><EmptyState icon={Home} title="No active lease yet" subtitle="Your move-in checklist appears once your landlord assigns you a home." testid="checklist-no-lease" /></div>;

  const locked = cl.status !== "draft";
  const s = summarize(cl.rooms);

  return (
    <div>
      <PageHeader title="Move-in checklist" subtitle={`Walk through ${data.lease.property_name} room by room. Rate each item, add notes and photos of any existing damage — it protects your deposit at move-out.`}
        action={!locked && (
          <Button variant="outline" onClick={() => save(cl)} disabled={saving} className="gap-2" data-testid="checklist-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save progress
          </Button>
        )} />

      <div className="space-y-5">
        {locked ? <SubmittedCard cl={cl} /> : <Summary s={s} />}

        {cl.rooms.map((room) => (
          <div key={room.key} className="rounded-2xl border border-slate-200 bg-white p-5" data-testid={`checklist-room-${room.key}`}>
            <div className="flex items-center gap-2 mb-1"><ClipboardCheck className="h-5 w-5 text-primary" /><h2 className="font-display font-bold text-lg text-slate-900">{room.label}</h2>
              <span className="ml-auto text-xs text-slate-400">{room.items.filter((i) => i.condition).length}/{room.items.length}</span></div>
            {room.items.map((it) => (
              <ChecklistItem key={it.key} checklistId={cl.id} roomKey={room.key} item={it} locked={locked}
                onPatch={(p) => patch(room.key, it.key, p)} onPhotosChange={refreshPhotos} />
            ))}
          </div>
        ))}

        <div className="rounded-2xl border border-slate-200 bg-white p-5">
          <h2 className="font-display font-bold text-lg text-slate-900 mb-2">Anything else?</h2>
          {locked ? <p className="text-sm text-slate-600 whitespace-pre-wrap" data-testid="checklist-general-note">{cl.general_note || "—"}</p>
            : <Textarea value={cl.general_note || ""} onChange={(e) => { dirty.current = true; setCl((c) => ({ ...c, general_note: e.target.value })); }} rows={3} maxLength={2000}
              placeholder="Keys received, utilities status, anything your landlord should know…" data-testid="checklist-general-note" />}
        </div>

        {!locked && (
          <div className="rounded-2xl border border-slate-200 bg-slate-900 text-white p-5 flex flex-wrap items-center gap-4" data-testid="checklist-submit-bar">
            <div className="flex-1 min-w-[200px]">
              <div className="font-display font-bold">Ready to sign off?</div>
              <div className="text-sm text-white/70">{s.rated < s.total ? `${s.total - s.rated} item(s) still need a rating.` : "Every item is rated. Submitting locks the checklist and creates a signed PDF."}</div>
            </div>
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button disabled={submitting || s.rated < s.total} className="gap-2 bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="checklist-submit">
                  {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Submit checklist
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="checklist-submit-dialog">
                <AlertDialogHeader><AlertDialogTitle>Submit your move-in checklist?</AlertDialogTitle>
                  <AlertDialogDescription>This locks the checklist, saves a signed PDF to your Documents and notifies your landlord. You won't be able to edit it afterwards.</AlertDialogDescription></AlertDialogHeader>
                <AlertDialogFooter><AlertDialogCancel data-testid="checklist-submit-cancel">Keep editing</AlertDialogCancel>
                  <AlertDialogAction onClick={submit} className="bg-emerald-600 hover:bg-emerald-700" data-testid="checklist-submit-confirm">Submit & sign</AlertDialogAction></AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          </div>
        )}
      </div>
    </div>
  );
}
